import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import "../styles/Purchases.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser,
    faPhone,
    faBuilding,
    faCalendarAlt,
    faPlus,
    faTrash,
    faSave,
    faTimes,
    faSpinner,
    faCheckCircle,
    faExclamationTriangle,
    faCreditCard,
    faWallet,
    faLandmark,
    faUniversity,
    faMoneyBill,
    faUserPlus,
    faEdit,
    faCopy,
    faBank,
    faQrcode,
    faAddressCard,
    faGlobe,
    faMobileAlt,
    faEnvelope,
    faFlag,
    faDollarSign,
    faStore
} from '@fortawesome/free-solid-svg-icons';

// Create translator instance
const createPartnersTranslator = () => {
    let cache = new Map();
    let translationListeners = new Set();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_partners_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Partners Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_partners_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        addPartner: 'Add Partner',
        editPartner: 'Edit Partner',
        partnerDetails: 'Partner Details',
        partnerInformation: 'Partner Information',
        bankAccounts: 'Bank & Wallet Accounts',
        partnerName: 'Partner Name',
        phone: 'Phone Number',
        businessName: 'Business Name',
        date: 'Date',
        addAccount: 'Add Account',
        accountType: 'Account Type',
        bankName: 'Bank Name',
        walletProvider: 'Wallet Provider',
        accountHolder: 'Account Holder Name',
        accountNumber: 'Account Number',
        ifscCode: 'IFSC Code',
        swiftCode: 'SWIFT Code',
        branch: 'Branch',
        walletMobile: 'Mobile Number',
        walletEmail: 'Email Address',
        accountCountry: 'Country',
        accountCurrency: 'Currency',
        isDefault: 'Default Account',
        remove: 'Remove',
        savePartner: 'Save Partner',
        updatePartner: 'Update Partner',
        clear: 'Clear',
        saving: 'Saving...',
        partnerSaved: 'Partner saved successfully!',
        partnerUpdated: 'Partner updated successfully!',
        failedSave: 'Failed to save partner',
        failedUpdate: 'Failed to update partner',
        networkError: 'Network error occurred',
        partnerNameRequired: 'Partner name is required',
        phoneRequired: 'Phone number is required',
        dateRequired: 'Date is required',
        accountNumberRequired: 'Account number is required',
        bankNameRequired: 'Bank name is required for bank accounts',
        walletProviderRequired: 'Wallet provider is required for digital wallets',
        walletMobileRequired: 'Mobile number is required for digital wallets',
        accountHolderRequired: 'Account holder name is required',
        invalidPhone: 'Please enter a valid phone number',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        bank: 'Bank Account',
        digital_wallet: 'Digital Wallet',
        selectAccountType: 'Select account type',
        selectBank: 'Select bank',
        selectWalletProvider: 'Select wallet provider',
        selectCountry: 'Select country',
        selectCurrency: 'Select currency',
        default: 'Default',
        copyAccount: 'Copy account number',
        accountCopied: 'Account number copied',
        maxAccountsReached: 'Maximum 8 accounts allowed',
        optional: 'Optional',
        atLeastOneAccount: 'At least one valid account is required'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    const addListener = (callback) => {
        translationListeners.add(callback);
    };

    const removeListener = (callback) => {
        translationListeners.delete(callback);
    };

    const notifyListeners = (lang) => {
        translationListeners.forEach(callback => callback(lang));
    };

    const checkLanguageChange = () => {
        const currentLang = localStorage.getItem('ourzest_language') || 'en';
        notifyListeners(currentLang);
    };

    const startLanguageCheck = () => {
        checkLanguageChange();
        const intervalId = setInterval(checkLanguageChange, 500);
        window.addEventListener('storage', checkLanguageChange);
        return () => {
            clearInterval(intervalId);
            window.removeEventListener('storage', checkLanguageChange);
        };
    };

    loadFromCache();

    return {
        getTranslations,
        translateText,
        addListener,
        removeListener,
        startLanguageCheck
    };
};

const partnersTranslator = createPartnersTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations?.success || 'Success';
            case 'error':
                return translations?.error || 'Error';
            case 'warning':
                return translations?.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// Accounts Table Component
const AccountsTable = ({
    accounts,
    onAccountChange,
    onRemove,
    onAdd,
    errors,
    disabled,
    translations,
    isRTL,
    showToast
}) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [banks, setBanks] = useState([]);
    const [walletProviders, setWalletProviders] = useState([]);
    const [countries, setCountries] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [loadingData, setLoadingData] = useState(false);

    const getAccountIcon = (type) => {
        switch (type) {
            case 'bank':
                return faLandmark;
            // case 'digital_wallet':
                return faWallet;
            default:
                return faUniversity;
        }
    };

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Load dropdown data
    useEffect(() => {
        const loadDropdownData = async () => {
            setLoadingData(true);
            try {
                // Load banks
                const banksResponse = await fetch('https://ourzestapp.online/oz_backend/partners.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'get_bank_names' })
                });
                const banksData = await banksResponse.json();
                if (banksData.status === 'success') {
                    setBanks(banksData.data);
                }

                // Load wallet providers
                const walletsResponse = await fetch('https://ourzestapp.online/oz_backend/partners.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'get_wallet_providers' })
                });
                const walletsData = await walletsResponse.json();
                if (walletsData.status === 'success') {
                    setWalletProviders(walletsData.data);
                }

                // Load countries
                const countriesResponse = await fetch('https://ourzestapp.online/oz_backend/partners.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'get_countries' })
                });
                const countriesData = await countriesResponse.json();
                if (countriesData.status === 'success') {
                    setCountries(countriesData.data);
                }

                // Load currencies
                const currenciesResponse = await fetch('https://ourzestapp.online/oz_backend/partners.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'get_currencies' })
                });
                const currenciesData = await currenciesResponse.json();
                if (currenciesData.status === 'success') {
                    setCurrencies(currenciesData.data);
                }
            } catch (error) {
                console.error('Error loading dropdown data:', error);
            } finally {
                setLoadingData(false);
            }
        };

        loadDropdownData();
    }, []);

    const copyAccountNumber = (accountNo) => {
        if (accountNo) {
            navigator.clipboard.writeText(accountNo);
            showToast('success', translations?.accountCopied || 'Account number copied');
        }
    };

    const displayAccounts = accounts && accounts.length > 0 ? accounts : [];

    if (displayAccounts.length === 0) return null;

    // Desktop view
    if (!isMobile) {
        return (
            <div className="items-table-container">
                <div className="table-header-actions">
                    <h4>
                        <FontAwesomeIcon icon={faCreditCard} className="header-icon-only" />
                        {translations?.bankAccounts || 'Bank & Wallet Accounts'} 
                        <span className="accounts-count">({displayAccounts.length}/8)</span>
                    </h4>
                    <button
                        type="button"
                        className="btn-add-item-table"
                        onClick={() => {
                            if (displayAccounts.length < 8) {
                                onAdd();
                            } else {
                                showToast('warning', translations?.maxAccountsReached || 'Maximum 8 accounts allowed');
                            }
                        }}
                        disabled={disabled || displayAccounts.length >= 8}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        <span>{translations?.addAccount || 'Add Account'}</span>
                    </button>
                </div>

                <div className="table-wrapper accounts-table-wrapper">
                    <table className="items-table">
                        <thead>
                            <tr>
                                <th style={{ width: '12%' }}>{translations?.accountType || 'Type'}</th>
                                <th style={{ width: '15%' }}>{translations?.accountHolder || 'Account Holder'}</th>
                                <th style={{ width: '18%' }}>{translations?.accountNumber || 'Account No'}</th>
                                <th style={{ width: '20%' }}>Details</th>
                                <th style={{ width: '10%' }}>{translations?.accountCountry || 'Country'}</th>
                                <th style={{ width: '10%' }}>{translations?.accountCurrency || 'Currency'}</th>
                                <th style={{ width: '8%' }}>{translations?.isDefault || 'Default'}</th>
                                <th style={{ width: '7%' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayAccounts.map((account, index) => (
                                <tr key={index} className={account.is_default ? 'default-row' : ''}>
                                    <td>
                                        <select
                                            value={account.account_type || 'bank'}
                                            onChange={(e) => onAccountChange(index, 'account_type', e.target.value)}
                                            disabled={disabled}
                                            style={{ width: '100%' }}
                                        >
                                            <option value="bank">{translations?.bank || 'Bank Account'}</option>
                                            <option value="digital_wallet">{translations?.digital_wallet || 'Digital Wallet'}</option>
                                        </select>
                                        {errors[`account_type_${index}`] && (
                                            <div className="table-error" style={{ fontSize: '11px', color: '#ef4444' }}>
                                                {errors[`account_type_${index}`]}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            value={account.account_holder || ''}
                                            onChange={(e) => onAccountChange(index, 'account_holder', e.target.value)}
                                            placeholder="Account holder"
                                            disabled={disabled}
                                            style={{ width: '100%' }}
                                        />
                                        {errors[`account_holder_${index}`] && (
                                            <div className="table-error" style={{ fontSize: '11px', color: '#ef4444' }}>
                                                {errors[`account_holder_${index}`]}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                            <input
                                                type="text"
                                                value={account.account_no || ''}
                                                onChange={(e) => onAccountChange(index, 'account_no', e.target.value)}
                                                placeholder="Account number"
                                                disabled={disabled}
                                                style={{ flex: 1 }}
                                            />
                                            {account.account_no && (
                                                <button
                                                    type="button"
                                                    onClick={() => copyAccountNumber(account.account_no)}
                                                    style={{ padding: '4px 8px', cursor: 'pointer' }}
                                                    title={translations?.copyAccount || 'Copy'}
                                                >
                                                    <FontAwesomeIcon icon={faCopy} />
                                                </button>
                                            )}
                                        </div>
                                        {errors[`account_no_${index}`] && (
                                            <div className="table-error" style={{ fontSize: '11px', color: '#ef4444' }}>
                                                {errors[`account_no_${index}`]}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {account.account_type === 'bank' && (
                                            <div>
                                                <select
                                                    value={account.bank_name || ''}
                                                    onChange={(e) => onAccountChange(index, 'bank_name', e.target.value)}
                                                    disabled={disabled}
                                                    style={{ width: '100%', marginBottom: '4px' }}
                                                >
                                                    <option value="">Select Bank *</option>
                                                    {banks.map(bank => (
                                                        <option key={bank.value} value={bank.value}>
                                                            {bank.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                <input
                                                    type="text"
                                                    value={account.ifsc_code || ''}
                                                    onChange={(e) => onAccountChange(index, 'ifsc_code', e.target.value.toUpperCase())}
                                                    placeholder="IFSC Code"
                                                    disabled={disabled}
                                                    style={{ width: '100%', marginTop: '4px' }}
                                                />
                                                {errors[`bank_name_${index}`] && (
                                                    <div className="table-error" style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px' }}>
                                                        {errors[`bank_name_${index}`]}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {account.account_type === 'digital_wallet' && (
                                            <div>
                                                <select
                                                    value={account.wallet_provider || ''}
                                                    onChange={(e) => onAccountChange(index, 'wallet_provider', e.target.value)}
                                                    disabled={disabled}
                                                    style={{ width: '100%', marginBottom: '4px' }}
                                                >
                                                    <option value="">Select Wallet *</option>
                                                    {walletProviders.map(provider => (
                                                        <option key={provider.value} value={provider.value}>
                                                            {provider.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                <input
                                                    type="tel"
                                                    value={account.wallet_mobile || ''}
                                                    onChange={(e) => onAccountChange(index, 'wallet_mobile', e.target.value)}
                                                    placeholder="Mobile Number *"
                                                    disabled={disabled}
                                                    style={{ width: '100%', marginTop: '4px' }}
                                                />
                                                {errors[`wallet_provider_${index}`] && (
                                                    <div className="table-error" style={{ fontSize: '11px', color: '#ef4444' }}>
                                                        {errors[`wallet_provider_${index}`]}
                                                    </div>
                                                )}
                                                {errors[`wallet_mobile_${index}`] && (
                                                    <div className="table-error" style={{ fontSize: '11px', color: '#ef4444' }}>
                                                        {errors[`wallet_mobile_${index}`]}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <select
                                            value={account.account_country || ''}
                                            onChange={(e) => onAccountChange(index, 'account_country', e.target.value)}
                                            disabled={disabled}
                                            style={{ width: '100%' }}
                                        >
                                            <option value="">Select country</option>
                                            {countries.map(country => (
                                                <option key={country.value} value={country.value}>
                                                    {country.label}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                    <td>
                                        <select
                                            value={account.account_currency || ''}
                                            onChange={(e) => onAccountChange(index, 'account_currency', e.target.value)}
                                            disabled={disabled}
                                            style={{ width: '100%' }}
                                        >
                                            <option value="">Select currency</option>
                                            {currencies.map(currency => (
                                                <option key={currency.value} value={currency.value}>
                                                    {currency.label}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="default-cell" style={{ textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            checked={account.is_default || false}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    displayAccounts.forEach((_, i) => {
                                                        if (i !== index) {
                                                            onAccountChange(i, 'is_default', false);
                                                        }
                                                    });
                                                }
                                                onAccountChange(index, 'is_default', e.target.checked);
                                            }}
                                            disabled={disabled}
                                        />
                                        {account.is_default && (
                                            <span className="default-indicator" style={{ fontSize: '10px', marginLeft: '4px' }}>
                                                {translations?.default || 'Default'}
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className="table-remove-btn"
                                            onClick={() => {
                                                if (displayAccounts.length > 1) {
                                                    onRemove(index);
                                                } else {
                                                    showToast('warning', 'At least one account is required');
                                                }
                                            }}
                                            disabled={disabled || displayAccounts.length === 1}
                                            title={translations?.remove || 'Remove'}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // Mobile view
    return (
        <div className="items-mobile-container">
            <div className="items-header-mobile">
                <h4>
                    <FontAwesomeIcon icon={faCreditCard} />
                    {translations?.bankAccounts || 'Bank & Wallet Accounts'} 
                    <span className="accounts-count">({displayAccounts.length}/8)</span>
                </h4>
                <button
                    type="button"
                    className="btn-add-item-mobile"
                    onClick={() => {
                        if (displayAccounts.length < 8) {
                            onAdd();
                        } else {
                            showToast('warning', translations?.maxAccountsReached || 'Maximum 8 accounts allowed');
                        }
                    }}
                    disabled={disabled || displayAccounts.length >= 8}
                >
                    <FontAwesomeIcon icon={faPlus} />
                    <span>{translations?.addAccount || 'Add Account'}</span>
                </button>
            </div>

            <div className="items-cards-container">
                {displayAccounts.map((account, index) => (
                    <div key={index} className={`item-card-mobile ${account.is_default ? 'default-account' : ''}`}>
                        <div className="item-card-header">
                            <span className="item-number">
                                <FontAwesomeIcon icon={getAccountIcon(account.account_type)} />
                                {account.account_type === 'bank' ? (translations?.bank || 'Bank Account') :
                                 (translations?.digital_wallet || 'Digital Wallet')} #{index + 1}
                                {account.is_default && (
                                    <span className="default-badge">{translations?.default || 'Default'}</span>
                                )}
                            </span>
                            <button
                                type="button"
                                className="item-remove-btn-mobile"
                                onClick={() => {
                                    if (displayAccounts.length > 1) {
                                        onRemove(index);
                                    } else {
                                        showToast('warning', 'At least one account is required');
                                    }
                                }}
                                disabled={disabled || displayAccounts.length === 1}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        </div>

                        <div className="item-card-content">
                            <div className="mobile-field">
                                <label>Account Type *</label>
                                <select
                                    value={account.account_type || 'bank'}
                                    onChange={(e) => onAccountChange(index, 'account_type', e.target.value)}
                                    disabled={disabled}
                                >
                                    <option value="bank">Bank Account</option>
                                    {/* <option value="digital_wallet">Digital Wallet</option> */}
                                </select>
                            </div>

                            <div className="mobile-field">
                                <label>Account Holder *</label>
                                <input
                                    type="text"
                                    value={account.account_holder || ''}
                                    onChange={(e) => onAccountChange(index, 'account_holder', e.target.value)}
                                    placeholder="Enter account holder name"
                                    disabled={disabled}
                                />
                                {errors[`account_holder_${index}`] && (
                                    <div className="error-text" style={{ color: '#ef4444', fontSize: '12px' }}>
                                        {errors[`account_holder_${index}`]}
                                    </div>
                                )}
                            </div>

                            <div className="mobile-field">
                                <label>Account Number *</label>
                                <input
                                    type="text"
                                    value={account.account_no || ''}
                                    onChange={(e) => onAccountChange(index, 'account_no', e.target.value)}
                                    placeholder="Enter account number"
                                    disabled={disabled}
                                />
                                {errors[`account_no_${index}`] && (
                                    <div className="error-text" style={{ color: '#ef4444', fontSize: '12px' }}>
                                        {errors[`account_no_${index}`]}
                                    </div>
                                )}
                            </div>

                            {account.account_type === 'bank' && (
                                <>
                                    <div className="mobile-field">
                                        <label>Bank Name *</label>
                                        <select
                                            value={account.bank_name || ''}
                                            onChange={(e) => onAccountChange(index, 'bank_name', e.target.value)}
                                            disabled={disabled}
                                        >
                                            <option value="">Select Bank</option>
                                            {banks.map(bank => (
                                                <option key={bank.value} value={bank.value}>
                                                    {bank.label}
                                                </option>
                                            ))}
                                        </select>
                                        {errors[`bank_name_${index}`] && (
                                            <div className="error-text" style={{ color: '#ef4444', fontSize: '12px' }}>
                                                {errors[`bank_name_${index}`]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mobile-field">
                                        <label>IFSC Code</label>
                                        <input
                                            type="text"
                                            value={account.ifsc_code || ''}
                                            onChange={(e) => onAccountChange(index, 'ifsc_code', e.target.value.toUpperCase())}
                                            placeholder="IFSC Code"
                                            disabled={disabled}
                                        />
                                    </div>
                                </>
                            )}

                            {account.account_type === 'digital_wallet' && (
                                <>
                                    <div className="mobile-field">
                                        <label>Wallet Provider *</label>
                                        <select
                                            value={account.wallet_provider || ''}
                                            onChange={(e) => onAccountChange(index, 'wallet_provider', e.target.value)}
                                            disabled={disabled}
                                        >
                                            <option value="">Select Wallet</option>
                                            {walletProviders.map(provider => (
                                                <option key={provider.value} value={provider.value}>
                                                    {provider.label}
                                                </option>
                                            ))}
                                        </select>
                                        {errors[`wallet_provider_${index}`] && (
                                            <div className="error-text" style={{ color: '#ef4444', fontSize: '12px' }}>
                                                {errors[`wallet_provider_${index}`]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mobile-field">
                                        <label>Mobile Number *</label>
                                        <input
                                            type="tel"
                                            value={account.wallet_mobile || ''}
                                            onChange={(e) => onAccountChange(index, 'wallet_mobile', e.target.value)}
                                            placeholder="Mobile Number"
                                            disabled={disabled}
                                        />
                                        {errors[`wallet_mobile_${index}`] && (
                                            <div className="error-text" style={{ color: '#ef4444', fontSize: '12px' }}>
                                                {errors[`wallet_mobile_${index}`]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mobile-field">
                                        <label>Email Address</label>
                                        <input
                                            type="email"
                                            value={account.wallet_email || ''}
                                            onChange={(e) => onAccountChange(index, 'wallet_email', e.target.value)}
                                            placeholder="Email Address"
                                            disabled={disabled}
                                        />
                                    </div>
                                </>
                            )}

                            <div className="mobile-field">
                                <label>Country</label>
                                <select
                                    value={account.account_country || ''}
                                    onChange={(e) => onAccountChange(index, 'account_country', e.target.value)}
                                    disabled={disabled}
                                >
                                    <option value="">Select country</option>
                                    {countries.map(country => (
                                        <option key={country.value} value={country.value}>
                                            {country.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="mobile-field">
                                <label>Currency</label>
                                <select
                                    value={account.account_currency || ''}
                                    onChange={(e) => onAccountChange(index, 'account_currency', e.target.value)}
                                    disabled={disabled}
                                >
                                    <option value="">Select currency</option>
                                    {currencies.map(currency => (
                                        <option key={currency.value} value={currency.value}>
                                            {currency.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="mobile-field checkbox-field">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={account.is_default || false}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                displayAccounts.forEach((_, i) => {
                                                    if (i !== index) {
                                                        onAccountChange(i, 'is_default', false);
                                                    }
                                                });
                                            }
                                            onAccountChange(index, 'is_default', e.target.checked);
                                        }}
                                        disabled={disabled}
                                    />
                                    <span className="checkbox-text">Set as default account</span>
                                </label>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {displayAccounts.length < 8 && (
                <button
                    type="button"
                    className="btn-add-item-bottom"
                    onClick={onAdd}
                    disabled={disabled}
                >
                    <FontAwesomeIcon icon={faPlus} />
                    <span>Add Another Account</span>
                </button>
            )}
        </div>
    );
};

const Partners = ({ partnerId = null, onSuccess = null }) => {
    const { user } = useAuth();

    const [shopId, setShopId] = useState(() => {
        if (user?.shop_id) {
            return user.shop_id;
        }
        return localStorage.getItem('current_shop_id') || '';
    });

    const [formData, setFormData] = useState({
        partner_id: '',
        date: new Date().toISOString().split('T')[0],
        partner_name: '',
        phone: '',
        business_name: '',
        accounts: [
            {
                account_type: 'bank',
                bank_name: '',
                bank_label: '',
                wallet_provider: '',
                wallet_provider_label: '',
                account_holder: '',
                account_no: '',
                ifsc_code: '',
                swift_code: '',
                branch: '',
                wallet_mobile: '',
                wallet_email: '',
                account_country: '',
                account_currency: '',
                is_default: true
            }
        ],
        themeColor: localStorage.getItem('ourzest_theme') || '#8b5cf6'
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    const [isTranslating, setIsTranslating] = useState(false);

    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    const toastTimerRef = useRef(null);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    }, [currentLanguage]);

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
        };
    }, []);

    const loadTranslations = useCallback(async (lang) => {
        if (!lang) return;

        setIsTranslating(true);
        try {
            const trans = await partnersTranslator.getTranslations(lang);
            setTranslations(trans);

            const isRTL = lang === 'ur' || lang === 'ar';
            document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
            document.body.dir = isRTL ? 'rtl' : 'ltr';

        } catch (error) {
            console.error('Error loading translations:', error);
        } finally {
            setIsTranslating(false);
        }
    }, []);

    useEffect(() => {
        loadTranslations(currentLanguage);

        const handleLanguageChange = () => {
            const newLang = localStorage.getItem('ourzest_language') || 'en';
            if (newLang !== currentLanguage) {
                setCurrentLanguage(newLang);
                loadTranslations(newLang);
            }
        };

        window.addEventListener('storage', handleLanguageChange);

        const intervalId = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 500);

        const translationListener = (lang) => {
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };

        partnersTranslator.addListener(translationListener);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(intervalId);
            partnersTranslator.removeListener(translationListener);
        };
    }, [currentLanguage, loadTranslations]);

    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#8b5cf6';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'purchase-primary-color': color,
                    'purchase-primary-dark': adjust(-40),
                    'purchase-primary-light': adjust(40),
                    'purchase-primary-ultralight': adjust(20),
                    'purchase-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'purchase-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });
            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(formData.themeColor);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#8b5cf6';
            if (theme !== formData.themeColor) {
                setFormData(prev => ({ ...prev, themeColor: theme }));
                applyTheme(theme);
            }
        };

        handleThemeChange();
        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);

        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [formData.themeColor]);

    useEffect(() => {
        if (partnerId) {
            setIsEditMode(true);
            loadPartnerData(partnerId);
        }
    }, [partnerId]);

    const loadPartnerData = async (id) => {
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/partners.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_record',
                    partner_id: id,
                    username: user?.username,
                    shop_id: shopId
                })
            });

            const data = await response.json();

            if (data.status === 'success' && data.data) {
                const partner = data.data;
                
                const accounts = partner.accounts && partner.accounts.length > 0 
                    ? partner.accounts.map(acc => ({
                        ...acc,
                        bank_label: acc.bank_name,
                        wallet_provider_label: acc.wallet_provider
                    }))
                    : [{
                        account_type: 'bank',
                        bank_name: '',
                        bank_label: '',
                        wallet_provider: '',
                        wallet_provider_label: '',
                        account_holder: partner.partner_name,
                        account_no: '',
                        ifsc_code: '',
                        swift_code: '',
                        branch: '',
                        wallet_mobile: '',
                        wallet_email: '',
                        account_country: '',
                        account_currency: '',
                        is_default: true
                    }];

                setFormData({
                    partner_id: partner.partner_id,
                    date: partner.date || new Date().toISOString().split('T')[0],
                    partner_name: partner.partner_name || '',
                    phone: partner.phone || '',
                    business_name: partner.business_name || '',
                    accounts: accounts,
                    themeColor: formData.themeColor
                });
            } else {
                showToast('error', data.message || 'Failed to load partner data');
            }
        } catch (error) {
            console.error('Error loading partner:', error);
            showToast('error', 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }

        if (name === 'partner_name' && formData.accounts.length > 0) {
            const updatedAccounts = [...formData.accounts];
            if (!updatedAccounts[0].account_holder) {
                updatedAccounts[0] = {
                    ...updatedAccounts[0],
                    account_holder: value || ''
                };
                setFormData(prev => ({
                    ...prev,
                    accounts: updatedAccounts
                }));
            }
        }
    };

    const handleAccountChange = (index, field, value) => {
        const updatedAccounts = [...formData.accounts];
        updatedAccounts[index] = {
            ...updatedAccounts[index],
            [field]: value
        };

        if (field === 'is_default' && value === true) {
            updatedAccounts.forEach((acc, i) => {
                if (i !== index) {
                    updatedAccounts[i] = { ...updatedAccounts[i], is_default: false };
                }
            });
        }

        setFormData(prev => ({
            ...prev,
            accounts: updatedAccounts
        }));

        const errorKey = `${field}_${index}`;
        if (errors[errorKey]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[errorKey];
                return newErrors;
            });
        }
    };

    const addAccount = () => {
        if (formData.accounts.length >= 8) {
            showToast('warning', translations?.maxAccountsReached || 'Maximum 8 accounts allowed');
            return;
        }

        setFormData(prev => ({
            ...prev,
            accounts: [
                ...prev.accounts,
                {
                    account_type: 'bank',
                    bank_name: '',
                    bank_label: '',
                    wallet_provider: '',
                    wallet_provider_label: '',
                    account_holder: prev.partner_name || '',
                    account_no: '',
                    ifsc_code: '',
                    swift_code: '',
                    branch: '',
                    wallet_mobile: '',
                    wallet_email: '',
                    account_country: '',
                    account_currency: '',
                    is_default: false
                }
            ]
        }));
    };

    const removeAccount = (index) => {
        if (formData.accounts.length <= 1) {
            showToast('warning', 'At least one account is required');
            return;
        }

        const updatedAccounts = [...formData.accounts];
        updatedAccounts.splice(index, 1);

        if (formData.accounts[index].is_default && updatedAccounts.length > 0) {
            updatedAccounts[0] = { ...updatedAccounts[0], is_default: true };
        }

        setFormData(prev => ({
            ...prev,
            accounts: updatedAccounts
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        // Validate partner basic info
        if (!formData.partner_name?.trim()) {
            newErrors.partner_name = translations?.partnerNameRequired || 'Partner name is required';
        }

        if (!formData.phone?.trim()) {
            newErrors.phone = translations?.phoneRequired || 'Phone number is required';
        } else {
            const phoneRegex = /^[0-9+\-\s()]{10,15}$/;
            if (!phoneRegex.test(formData.phone.replace(/[\s\-()]/g, ''))) {
                newErrors.phone = translations?.invalidPhone || 'Please enter a valid phone number';
            }
        }

        if (!formData.date) {
            newErrors.date = translations?.dateRequired || 'Date is required';
        }

        // Validate accounts
        let hasValidAccount = false;
        
        formData.accounts.forEach((account, index) => {
            // Check if this account has any data
            const hasAccountData = account.account_no?.trim() || 
                                  account.account_holder?.trim() || 
                                  (account.account_type === 'bank' && account.bank_name) ||
                                  (account.account_type === 'digital_wallet' && (account.wallet_provider || account.wallet_mobile));
            
            // If this is the only account and it's empty, show error
            if (formData.accounts.length === 1 && !hasAccountData) {
                newErrors[`account_no_${index}`] = translations?.atLeastOneAccount || 'At least one valid account is required';
                newErrors[`account_holder_${index}`] = translations?.accountHolderRequired || 'Account holder is required';
            }
            
            // If account has data, validate it
            if (hasAccountData) {
                hasValidAccount = true;
                
                if (!account.account_holder?.trim()) {
                    newErrors[`account_holder_${index}`] = translations?.accountHolderRequired || 'Account holder name is required';
                }

                if (!account.account_no?.trim()) {
                    newErrors[`account_no_${index}`] = translations?.accountNumberRequired || 'Account number is required';
                }

                if (account.account_type === 'bank') {
                    if (!account.bank_name) {
                        newErrors[`bank_name_${index}`] = translations?.bankNameRequired || 'Bank name is required';
                    }
                }

                if (account.account_type === 'digital_wallet') {
                    if (!account.wallet_provider) {
                        newErrors[`wallet_provider_${index}`] = translations?.walletProviderRequired || 'Wallet provider is required';
                    }
                    if (!account.wallet_mobile) {
                        newErrors[`wallet_mobile_${index}`] = translations?.walletMobileRequired || 'Mobile number is required';
                    }
                }
            }
        });

        // If no valid account found, show error
        if (!hasValidAccount && formData.accounts.length > 0) {
            newErrors.noAccount = translations?.atLeastOneAccount || 'At least one valid account is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            console.log('Validation errors:', errors);
            showToast('error', 'Please fix validation errors');
            const firstError = document.querySelector('.has-error, .error-message-modern');
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        setSaving(true);

        try {
            const currentUsername = user?.username || 'Unknown';
            const currentShopId = shopId || localStorage.getItem('current_shop_id');

            if (!currentUsername || currentUsername === 'Unknown') {
                throw new Error("User not authenticated");
            }

            if (!currentShopId) {
                throw new Error("Shop ID is required. Please select a shop first.");
            }

            const action = isEditMode ? 'edit' : 'add';

            // Filter out empty accounts and ensure all required fields are present
            const processedAccounts = formData.accounts
                .filter(account => {
                    // Only include accounts that have at least account number
                    return account.account_no?.trim() || 
                           (account.account_type === 'bank' && account.bank_name) ||
                           (account.account_type === 'digital_wallet' && account.wallet_provider);
                })
                .map((account, idx) => ({
                    account_type: account.account_type || 'bank',
                    bank_name: account.account_type === 'bank' ? (account.bank_name || '') : null,
                    account_holder: (account.account_holder || formData.partner_name || '').trim(),
                    account_no: (account.account_no || '').trim(),
                    ifsc_code: account.ifsc_code || '',
                    swift_code: account.swift_code || '',
                    branch: account.branch || '',
                    wallet_provider: account.account_type === 'digital_wallet' ? (account.wallet_provider || '') : null,
                    wallet_mobile: account.account_type === 'digital_wallet' ? (account.wallet_mobile || '') : null,
                    wallet_email: account.account_type === 'digital_wallet' ? (account.wallet_email || '') : null,
                    account_country: account.account_country || '',
                    account_currency: account.account_currency || '',
                    is_default: idx === 0 ? true : (account.is_default || false)
                }));

            // If no accounts after filtering, add a default one
            if (processedAccounts.length === 0) {
                processedAccounts.push({
                    account_type: 'bank',
                    bank_name: '',
                    account_holder: formData.partner_name || '',
                    account_no: '',
                    ifsc_code: '',
                    swift_code: '',
                    branch: '',
                    wallet_provider: null,
                    wallet_mobile: null,
                    wallet_email: null,
                    account_country: '',
                    account_currency: '',
                    is_default: true
                });
            }

            const payload = {
                action: action,
                username: currentUsername,
                shop_id: parseInt(currentShopId),
                date: formData.date,
                partner_name: formData.partner_name.trim(),
                phone: formData.phone.trim(),
                business_name: formData.business_name?.trim() || '',
                accounts: processedAccounts
            };

            if (isEditMode && partnerId) {
                payload.partner_id = partnerId;
            }

            console.log('Sending payload:', JSON.stringify(payload, null, 2));

            const response = await fetch('https://ourzestapp.online/oz_backend/partners.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            const responseText = await response.text();
            console.log('Raw response:', responseText);
            
            let data;
            try {
                data = JSON.parse(responseText);
                console.log('Parsed response:', data);
            } catch (parseError) {
                console.error('Failed to parse response:', parseError);
                throw new Error(`Server returned invalid JSON: ${responseText.substring(0, 200)}`);
            }

            if (data.status === 'success') {
                const successMessage = isEditMode
                    ? translations?.partnerUpdated || 'Partner updated successfully!'
                    : translations?.partnerSaved || 'Partner saved successfully!';

                showToast('success', successMessage);
                console.log('Success:', successMessage);

                if (!isEditMode) {
                    // Reset form
                    setFormData({
                        partner_id: '',
                        date: new Date().toISOString().split('T')[0],
                        partner_name: '',
                        phone: '',
                        business_name: '',
                        accounts: [
                            {
                                account_type: 'bank',
                                bank_name: '',
                                bank_label: '',
                                wallet_provider: '',
                                wallet_provider_label: '',
                                account_holder: '',
                                account_no: '',
                                ifsc_code: '',
                                swift_code: '',
                                branch: '',
                                wallet_mobile: '',
                                wallet_email: '',
                                account_country: '',
                                account_currency: '',
                                is_default: true
                            }
                        ],
                        themeColor: formData.themeColor
                    });
                    setErrors({});
                }

                if (onSuccess) {
                    onSuccess(data.partner_id || partnerId);
                }
            } else {
                const errorMessage = data.message ||
                    (isEditMode
                        ? translations?.failedUpdate || 'Failed to update partner'
                        : translations?.failedSave || 'Failed to save partner');
                showToast('error', errorMessage);
                console.error('Server error:', data);
            }
        } catch (error) {
            console.error('Submission error:', error);
            showToast('error', error.message || translations?.networkError || 'Network error occurred');
        } finally {
            setSaving(false);
        }
    };

    const handleClear = () => {
        setFormData({
            partner_id: '',
            date: new Date().toISOString().split('T')[0],
            partner_name: '',
            phone: '',
            business_name: '',
            accounts: [
                {
                    account_type: 'bank',
                    bank_name: '',
                    bank_label: '',
                    wallet_provider: '',
                    wallet_provider_label: '',
                    account_holder: '',
                    account_no: '',
                    ifsc_code: '',
                    swift_code: '',
                    branch: '',
                    wallet_mobile: '',
                    wallet_email: '',
                    account_country: '',
                    account_currency: '',
                    is_default: true
                }
            ],
            themeColor: formData.themeColor
        });
        setErrors({});
        setIsEditMode(false);
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`purchases-form-independent ${isRTL ? 'rtl' : ''}`}>
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            <div className="form-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={isEditMode ? faEdit : faUserPlus} />
                    </div>
                    <div className="header-text">
                        <h1>{isEditMode ? (translations?.editPartner || 'Edit Partner') : (translations?.addPartner || 'Add Partner')}</h1>
                        <p>{translations?.partnerDetails || 'Partner Details'}</p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="modern-form">
                <div className="form-section">
                    <h3 className="section-title">
                        <FontAwesomeIcon icon={faUser} />
                        {translations?.partnerInformation || 'Partner Information'}
                    </h3>

                    <div className="form-grid-modern">
                        <div className={`form-group-modern ${errors.date ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faCalendarAlt} className="input-icon" />
                                <input
                                    type="date"
                                    id="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir="ltr"
                                />
                                <label htmlFor="date" className="input-label">
                                    {translations?.date || 'Date'} *
                                </label>
                            </div>
                            {errors.date && <div className="error-message-modern">{errors.date}</div>}
                        </div>

                        <div className={`form-group-modern ${errors.partner_name ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faUser} className="input-icon" />
                                <input
                                    type="text"
                                    id="partner_name"
                                    name="partner_name"
                                    value={formData.partner_name}
                                    onChange={handleChange}
                                    placeholder={translations?.partnerName || 'Enter partner name'}
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label htmlFor="partner_name" className="input-label">
                                    {translations?.partnerName || 'Partner Name'} *
                                </label>
                            </div>
                            {errors.partner_name && <div className="error-message-modern">{errors.partner_name}</div>}
                        </div>

                        <div className={`form-group-modern ${errors.phone ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faPhone} className="input-icon" />
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder={translations?.phone || 'Enter phone number'}
                                    disabled={saving}
                                    dir="ltr"
                                />
                                <label htmlFor="phone" className="input-label">
                                    {translations?.phone || 'Phone Number'} *
                                </label>
                            </div>
                            {errors.phone && <div className="error-message-modern">{errors.phone}</div>}
                        </div>

                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faBuilding} className="input-icon" />
                                <input
                                    type="text"
                                    id="business_name"
                                    name="business_name"
                                    value={formData.business_name}
                                    onChange={handleChange}
                                    placeholder={translations?.businessName || 'Enter business name'}
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label htmlFor="business_name" className="input-label">
                                    {translations?.businessName || 'Business Name'}
                                    <span className="optional"> ({translations?.optional || 'Optional'})</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <AccountsTable
                        accounts={formData.accounts}
                        onAccountChange={handleAccountChange}
                        onRemove={removeAccount}
                        onAdd={addAccount}
                        errors={errors}
                        disabled={saving}
                        translations={translations}
                        isRTL={isRTL}
                        showToast={showToast}
                    />
                </div>

                {errors.noAccount && (
                    <div className="error-message-modern" style={{ textAlign: 'center', marginTop: '10px' }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        {errors.noAccount}
                    </div>
                )}

                <div className="form-actions-modern">
                    <button
                        type="button"
                        className="btn-modern btn-secondary-modern"
                        onClick={handleClear}
                        disabled={saving}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                        <span>{translations?.clear || 'Clear'}</span>
                    </button>
                    <button
                        type="submit"
                        className="btn-modern btn-primary-modern"
                        disabled={saving}
                    >
                        {saving ? (
                            <>
                                <div className="btn-spinner"></div>
                                <span>{translations?.saving || 'Saving...'}</span>
                            </>
                        ) : (
                            <>
                                <span>{isEditMode ? (translations?.updatePartner || 'Update') : (translations?.savePartner || 'Save')}</span>
                                <FontAwesomeIcon icon={faSave} />
                            </>
                        )}
                    </button>
                </div>
            </form>

            {loading && (
                <div className="loading-overlay">
                    <div className="loading-content">
                        <div className="loading-spinner"></div>
                        <p>Loading...</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Partners;